import React from "react"

// Geometry
import {millimetresToMillivolts, millimetresToMs} from "../ModernEcgGrid/geometry"

// Styles
import useStyles, {GUIDE_PX} from "./styles"

/** A completed caliper drag. */
export type EcgMeasurement =
    | {mode: "time", ms: number, bpm: number}
    | {mode: "amp", mv: number, mm: number}


export type ModernCaliperApi = {

    clear(): boolean
}

/** Below these a drag is a stray click, not a measurement. */
const MIN_MS = 5;
const MIN_MM = 0.3;
/** Pixels of travel before the drag commits to an axis. */
const AXIS_LOCK_PX = 5;
/** Screen pixels between the cursor and the readout tag. */
const TAG_OFFSET_PX = 30;

type ModernCaliperLayerProps = {
    /** mm/s — converts horizontal travel to milliseconds. */
    speed: number
    /** mm/mV — converts vertical travel to millivolts. */
    gain: number
    /**
     * The stage's display scale — see `useEcgZoom`.
     *
     * This layer sits INSIDE the scaled box, so its bounding rect is the
     * on-screen one while its own `left` / `top` writes are in the box's
     * unscaled coordinates. Every pointer offset below is therefore divided by
     * this before it is used, which is also what keeps a reading identical at
     * 40 % and at 400 % — zooming magnifies the picture, never the millimetres.
     */
    zoom: number
    /** Width of the plot in ECG millimetres. */
    xSpanMm: number
    /** Height of the plot in ECG millimetres. */
    ySpanMm: number
    /** Filled with {@link ModernCaliperApi} while this layer is mounted. */
    apiRef: React.MutableRefObject<ModernCaliperApi | null>
    /** Fired once, on mouse-up, with the finished measurement. */
    onMeasure(measurement: EcgMeasurement): void
    /**
     * The guides just came down on this layer's own initiative — a replacement
     * drag took hold, or one was abandoned — so whatever readout the page is
     * showing no longer has lines on the trace to explain it.
     *
     * Every OTHER caliper state change reaches the page through `onMeasure`, so
     * without this a reading can outlive its own guides and leave the measure
     * bar floating over a trace with nothing marked on it.
     */
    onClear(): void
}

/**
 * Time / amplitude calipers, drawn over the Plotly trace.
 *
 * Drag sideways to measure an interval, up or down to measure a deflection;
 * the first ~5 px of travel decide which. Ported from the design prototype's
 * `ecg/EcgCanvas.js` caliper effect.
 *
 * Three deliberate implementation choices:
 *
 * 1. Pointer events, not mouse events. Mouse and touch listeners side by side
 *    would mean two code paths to keep in step, and a pen would land in whichever
 *    one the browser felt like emitting; one pointer path covers mouse, finger,
 *    Apple Pencil and Surface Pen alike, which matters because a stylus is the
 *    natural way to work a caliper on a tablet. The layer also captures the
 *    pointer on the way down, so a drag that runs off the trace — the normal way
 *    to measure a long interval — keeps tracking and still commits.
 *
 * 2. The guide lines and the readout tag are moved by mutating `style`
 *    directly, not through React state. A caliper drag fires a pointer-move per
 *    frame, and re-rendering this subtree at that rate would also re-render the
 *    sibling <Plot/> — repainting a 12-lead Plotly figure 60 times a second
 *    makes the drag visibly stutter. Only the finished measurement, on pointer-up,
 *    crosses back into React.
 *
 * 3. Millimetres per pixel are measured from this element's own bounding rect
 *    rather than assumed from the geometry constants. The layer is stretched
 *    over the plot exactly, and the plot's margins are zero, so its rect IS the
 *    axis box — which keeps the calipers honest even if Plotly's responsive
 *    handler scales the figure away from its declared width.
 *
 * @param props
 */
export default function ModernCaliperLayer(props: ModernCaliperLayerProps) {
    const styles = useStyles();
    const {speed, gain, zoom, xSpanMm, ySpanMm, apiRef, onMeasure, onClear} = props;

    const layerRef = React.useRef<HTMLDivElement | null>(null);
    const startVerticalRef = React.useRef<HTMLDivElement | null>(null);
    const endVerticalRef = React.useRef<HTMLDivElement | null>(null);
    const startHorizontalRef = React.useRef<HTMLDivElement | null>(null);
    const endHorizontalRef = React.useRef<HTMLDivElement | null>(null);
    const tagRef = React.useRef<HTMLDivElement | null>(null);

    // Read inside the mouse handlers, which are bound once.
    const onMeasureRef = React.useRef(onMeasure);
    const onClearRef = React.useRef(onClear);
    const speedRef = React.useRef(speed);
    const gainRef = React.useRef(gain);
    const zoomRef = React.useRef(zoom);
    const xSpanRef = React.useRef(xSpanMm);
    const ySpanRef = React.useRef(ySpanMm);

    onMeasureRef.current = onMeasure;
    onClearRef.current = onClear;
    speedRef.current = speed;
    gainRef.current = gain;
    zoomRef.current = zoom > 0 ? zoom : 1;
    xSpanRef.current = xSpanMm;
    ySpanRef.current = ySpanMm;

    /*
     * Hold the guides and the readout at a constant SCREEN size.
     *
     * Everything in here is inside the scaled box, so a 1.5 px guide is 6 px of
     * screen at 400 % — a caliper you cannot place precisely is worse than no
     * caliper. Written straight to `style` for the same reason the drag is: a
     * React re-render here re-renders the sibling Plotly figure.
     */
    React.useEffect(() => {
        const scale = zoom > 0 ? zoom : 1;

        [startVerticalRef.current, endVerticalRef.current].forEach(guide => {
            if (guide !== null) {
                guide.style.borderLeftWidth = `${GUIDE_PX / scale}px`
            }
        });

        [startHorizontalRef.current, endHorizontalRef.current].forEach(guide => {
            if (guide !== null) {
                guide.style.borderTopWidth = `${GUIDE_PX / scale}px`
            }
        });

        const tag = tagRef.current;

        if (tag !== null) {
            tag.style.transformOrigin = "50% 0";
            tag.style.transform = `translate(-50%,0) scale(${1 / scale})`
        }
    }, [zoom]);

    React.useEffect(() => {
        const layer = layerRef.current;

        if (layer === null) {
            return undefined
        }

        const guides = [
            startVerticalRef.current,
            endVerticalRef.current,
            startHorizontalRef.current,
            endHorizontalRef.current,
            tagRef.current,
        ];

        /**
         * The pointer currently drawing, or null when idle.
         *
         * This is the drag sentinel rather than `startX`, which the mouse-only
         * version used: a caliper legitimately starts at x = 0 — the very left
         * edge of the trace — and `startX === null` cannot tell that apart from
         * "not dragging".
         */
        let pointerId: number | null = null;
        let startX = 0;
        let startY = 0;
        let axis: "x" | "y" | null = null;
        let pending: EcgMeasurement | null = null;

        function hideGuides() {
            guides.forEach(guide => {
                if (guide !== null) {
                    guide.hidden = true
                }
            })
        }

        /** True for the event stream of the pointer that owns the current drag. */
        function isActive(event: PointerEvent): boolean {
            return pointerId !== null && event.pointerId === pointerId
        }

        /**
         * Takes `id` — not the event — because {@link clear} has to end a drag
         * it never received an event for.
         *
         * @param id
         */
        function reset(id: number) {
            if (layer !== null && layer.hasPointerCapture(id)) {
                layer.releasePointerCapture(id)
            }

            pointerId = null;
            axis = null;
            pending = null
        }

        /** {@link ModernCaliperApi.clear} — see the type for why this exists. */
        function clear(): boolean {
            const drawn = guides.some(guide => guide !== null && guide.hidden === false);
            const dragging = pointerId !== null;

            hideGuides();

            if (pointerId !== null) {
                reset(pointerId)
            }

            return drawn || dragging
        }

        function handleDown(event: PointerEvent) {
            // Ignore anything but the primary button, so a right-click menu or
            // a middle-click does not strand a half-drawn caliper on screen.
            // `button === 0` is also what a finger and a pen contact report.
            //
            // `isPrimary` drops the second finger of a two-finger gesture, and
            // the `pointerId` check drops anything that arrives mid-drag — either
            // would otherwise retarget a measurement already in progress.
            if (event.button !== 0 || !event.isPrimary || pointerId !== null || layer === null) {
                return
            }

            const rect = layer.getBoundingClientRect();
            const scale = zoomRef.current;

            pointerId = event.pointerId;
            startX = (event.clientX - rect.left) / scale;
            startY = (event.clientY - rect.top) / scale;
            axis = null;
            pending = null;

            // Route the rest of this pointer's events here even once it leaves
            // the layer, which is what lets move/up stay bound to the element
            // instead of the window. Throws if the pointer is already gone by
            // the time we ask — a lost capture just means this drag never starts.
            try {
                layer.setPointerCapture(event.pointerId)
            } catch (error) {
                pointerId = null;

                return
            }

            event.preventDefault()
        }

        function handleMove(event: PointerEvent) {
            const tag = tagRef.current;

            if (!isActive(event) || layer === null || tag === null) {
                return
            }

            const rect = layer.getBoundingClientRect();
            const scale = zoomRef.current;
            const x = (event.clientX - rect.left) / scale;
            const y = (event.clientY - rect.top) / scale;
            const dx = x - startX;
            const dy = y - startY;

            if (axis === null) {
                // Divided by the scale so the axis commits after the same amount
                // of hand movement whatever the stage is zoomed to.
                if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX / scale) {
                    return
                }

                axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
                hideGuides();
                onClearRef.current()
            }

            // Zero margins mean the rect and the axis box coincide, so this is
            // the true millimetres-per-pixel of the rendered figure. `rect` is
            // the on-screen box, so undo the zoom first — both this and the
            // offsets above are then in the same unscaled units and a reading
            // does not move when the reviewer zooms.
            const pxPerMmX = (rect.width / scale) / xSpanRef.current;
            const pxPerMmY = (rect.height / scale) / ySpanRef.current;

            if (axis === "x") {
                const startLine = startVerticalRef.current;
                const endLine = endVerticalRef.current;

                if (startLine === null || endLine === null) {
                    return
                }

                startLine.style.left = `${startX}px`;
                startLine.hidden = false;
                endLine.style.left = `${x}px`;
                endLine.hidden = false;

                const ms = millimetresToMs(Math.abs(dx) / pxPerMmX, speedRef.current);
                const visible = ms >= 1;

                tag.textContent = visible ? `${ms.toFixed(0)} ms · ${(60000 / ms).toFixed(0)} bpm` : "";
                tag.style.left = `${(x + startX) / 2}px`;
                tag.style.top = `${Math.max(4 / scale, y - (TAG_OFFSET_PX / scale))}px`;
                tag.hidden = !visible;
                pending = ms > MIN_MS ? {mode: "time", ms, bpm: 60000 / ms} : null
            } else {
                const startLine = startHorizontalRef.current;
                const endLine = endHorizontalRef.current;

                if (startLine === null || endLine === null) {
                    return
                }

                startLine.style.top = `${startY}px`;
                startLine.hidden = false;
                endLine.style.top = `${y}px`;
                endLine.hidden = false;

                const millimetres = Math.abs(dy) / pxPerMmY;
                const millivolts = millimetresToMillivolts(millimetres, gainRef.current);
                const visible = millimetres >= 0.2;

                tag.textContent = visible ? `${millivolts.toFixed(2)} mV · ${millimetres.toFixed(1)} mm` : "";
                tag.style.left = `${x}px`;
                tag.style.top = `${Math.max(4 / scale, ((y + startY) / 2) - (10 / scale))}px`;
                tag.hidden = !visible;
                pending = millimetres > MIN_MM ? {mode: "amp", mv: millivolts, mm: millimetres} : null
            }
        }

        function handleUp(event: PointerEvent) {
            if (!isActive(event)) {
                return
            }

            if (pending !== null) {
                onMeasureRef.current(pending)
            }

            // guides deliberately stay on screen — they are the measurement the
            // reviewer just took, and the readout tag is pinned to them. Only
            reset(event.pointerId)
        }

        /**
         * The gesture was taken away mid-drag — an incoming call, a system edge
         * swipe, a palm landing on the tablet.
         *
         * Nothing is committed: a drag that ended somewhere the reviewer did not
         * choose is not a measurement they made. The half-drawn guides come down
         * too, since leaving them up would present exactly that.
         */
        function handleCancel(event: PointerEvent) {
            if (!isActive(event)) {
                return
            }

            hideGuides();
            onClearRef.current();
            reset(event.pointerId)
        }

        /**
         * Capture ended without a pointerup or a pointercancel reaching us.
         *
         * Releasing the button outside the browser window is the usual way in.
         * Without this the drag never finishes: `pointerId` stays set, every
         * later pointerdown is rejected as "a second pointer mid-drag", and the
         * calipers go dead until the reviewer toggles the whole layer off and
         * back on. Fires after a normal pointerup too, where `isActive` is
         * already false and this does nothing.
         */
        function handleLostCapture(event: PointerEvent) {
            if (!isActive(event)) {
                return
            }

            hideGuides();
            onClearRef.current();
            reset(event.pointerId)
        }

        apiRef.current = {clear};

        // All five bind to the layer, not the window: the capture taken in
        // handleDown already redirects this pointer's events here wherever it
        // travels, so window listeners would only add a second path to the same
        // handlers — and would fire them for pointers that never touched the ECG.
        layer.addEventListener("pointerdown", handleDown);
        layer.addEventListener("pointermove", handleMove);
        layer.addEventListener("pointerup", handleUp);
        layer.addEventListener("pointercancel", handleCancel);
        layer.addEventListener("lostpointercapture", handleLostCapture);

        return () => {
            apiRef.current = null;

            layer.removeEventListener("pointerdown", handleDown);
            layer.removeEventListener("pointermove", handleMove);
            layer.removeEventListener("pointerup", handleUp);
            layer.removeEventListener("pointercancel", handleCancel);
            layer.removeEventListener("lostpointercapture", handleLostCapture)
        }
    }, [apiRef]);

    return (
        <div className={styles.layer} ref={layerRef}>
            <div className={styles.vline} ref={startVerticalRef} hidden/>
            <div className={styles.vline} ref={endVerticalRef} hidden/>
            <div className={styles.hline} ref={startHorizontalRef} hidden/>
            <div className={styles.hline} ref={endHorizontalRef} hidden/>
            <div className={styles.tag} ref={tagRef} hidden/>
        </div>
    )
}
